import {StandaloneHandler} from "../../common/handlers/standalone-handler";
import {Injectable} from "@angular/core";
import {HandleQuery} from "../../common/handlers/handle-query";
import {
  IE3ConsignmentProcess,
  IE3ConsignmentStatus,
  IE3ControlNotification,
  IE3EnsStatus,
  IE3FilingStatusTemporaryStorage,
  IE3TemporaryStorageFilingStatus,
  TaskMessageStatus
} from "@portbase/bezoekschip-service-typescriptmodels";
import {combineLatest, Observable, of} from "rxjs";
import {map} from "rxjs/operators";
import lodash from "lodash";
import {ConsignmentEnsUtils} from "../consignments/consignment.ens.utils";
import {ConsignmentTsUtils} from "../consignments/consignment.ts.utils";
import moment from "moment";
import {sendHandlerQuery} from "../../common/handlers/app-common-utils";
import {ErrorWithPointers} from "../../common/errors/errors";

@Injectable({
  providedIn: 'root'
})
export class ConsignmentHandler extends StandaloneHandler {

  @HandleQuery("getParsedConsignmentErrors")
  getParsedConsignmentErrors(query: GetErrorPointers): Observable<ErrorWithPointers[]> {
    const ensErrorPointers = sendHandlerQuery("getEnsErrorPointers", <GetEnsErrors>{
      consignmentProcess: query.consignmentProcess,
      status: query.status,
      taskStatus: query.taskStatus
    });
    const temporaryStorageErrors = sendHandlerQuery("getTemporaryStorageErrors", <GetTemporaryStorageErrors>{
      consignmentProcess: query.consignmentProcess,
      tsStatus: query.status?.filingStatusTemporaryStorage,
      tsFilingStatus: query.tsFilingStatus
    });
    return combineLatest([ensErrorPointers, temporaryStorageErrors])
      .pipe(map((result: ErrorWithPointers[][]) => lodash.flatMap(result,
        (r, index) => r.map(e => index === 0 ? ({
          ...e,
          header: `${e.header} – Reported by European customs`,
          summary: `${e.summary} – Reported by European customs`
        }) : ({
          ...e,
          header: `${e.header} – Reported by Dutch customs`,
          summary: `${e.summary} – Reported by Dutch customs`
        })))));
  }

  @HandleQuery("getEnsErrorPointers")
  getEnsErrorPointers(query: GetEnsErrors): ErrorWithPointers[] {
    if (!query.status) {
      return [];
    }
    const examinationPlace = (controlNotification: IE3ControlNotification) =>
      controlNotification.controls.map(control => control.examinationPlace)
        .map(place => place.placeOfExamination || place.referenceNumber).join(", ");

    switch (query.taskStatus) {
      case TaskMessageStatus.ERROR:
        switch (query.status.latestEnsStatus) {
          case IE3EnsStatus.DO_NOT_LOAD:
            return query.status.doNotLoadRequest.doNotLoadDetails.transportEquipment.length
              ? [{
                header: "Do not load this consignment",
                summary: `Do not load equipment: ${query.status.doNotLoadRequest.doNotLoadDetails.transportEquipment.map(t => t.containerIdentificationNumber).join(", ")}`,
                pointers: []
              }]
              : [{
                header: "Do not load this consignment",
                summary: "Do not load this consignment",
                pointers: []
              }];
          default:
            return [];
        }
      case TaskMessageStatus.REJECTED:
        switch (query.status.latestEnsStatus) {
          case IE3EnsStatus.HOUSE_CONSIGNMENT_INCORRECT_STATE_NOTIFICATION:
            return query.status.houseConsignmentInIncorrectStateNotification?.errors?.map(e => (<ErrorWithPointers> {
              header: e.humanReadableDescription || e.description || e.technicalErrorMessage,
              summary: e.humanReadableDescription || e.description || e.technicalErrorMessage,
              pointers: []
            }));
          case IE3EnsStatus.ERROR_NOTIFICATION:
            return query.status.filingStatusENS.errornotification.errors.map(e => {
              const description = e.humanReadableDescription || e.description;
              return (<ErrorWithPointers>{
                header: description ? description : e.technicalErrorMessage,
                summary: description
                  ? e.pointer != null ? description + ' at ' + e.pointer : description
                  : e.technicalErrorMessage,
                pointers: ConsignmentEnsUtils.parseErrorPointers(query.consignmentProcess, e.pointer)
              });
            });
          case IE3EnsStatus.LIFECYCLE_VALIDATION_ERROR:
            return query.status.filingStatusENS.lifecycleValidationErrorNotification.errors.map(e => (<ErrorWithPointers> {
              header: `Code: ${e.validationCode} – ${e.humanReadableDescription || e.description}`,
              summary: `Code: ${e.validationCode} – ${e.humanReadableDescription || e.description}`,
              pointers: ConsignmentEnsUtils.parseErrorPointers(query.consignmentProcess, e.pointer)
            }));
          default:
            return [];
        }
      case TaskMessageStatus.WARNING:
        switch (query.status.latestEnsStatus) {
          case IE3EnsStatus.INFORMATION_REQUESTED:
            return [{
              header: `Information requested at: ${this.formatDate(query.status.additionalInformationRequest.documentIssueTime)}`,
              summary: `Information requested at: ${this.formatDate(query.status.additionalInformationRequest.documentIssueTime)}`,
              pointers: []
            }];
          case IE3EnsStatus.INFORMATION_REQUEST_NOTIFIED:
            return [{
              header: `Information request notified at: ${this.formatDate(query.status.additionalInformationRequestNotification.documentIssueTime)}`,
              summary: `Information request notified at: ${this.formatDate(query.status.additionalInformationRequestNotification.documentIssueTime)}`,
              pointers: []
            }];
          case IE3EnsStatus.CONTROL_NOTIFICATION:
            return [{
              header: `<span>Control scheduled at: ${this.formatDate(query.status.controlNotification.scheduledControlTime)}</span><br/><i class="fa-light fa-fw fa-comment me-1 invisible"></i><span>Examination place: ${(examinationPlace(query.status.controlNotification))}</span>`,
              summary: `<span>Control scheduled at: ${this.formatDate(query.status.controlNotification.scheduledControlTime)}</span><br/><i class="fa-light fa-fw fa-comment me-1 invisible"></i><span>Examination place: ${(examinationPlace(query.status.controlNotification))}</span>`,
              pointers: []
            }];
          case IE3EnsStatus.ADVANCE_CONTROL_NOTIFICATION:
            return [{
              header: `<span>Control scheduled at: ${this.formatDate(query.status.advanceControlNotification.scheduledControlTime)}</span><br/><i class="fa-light fa-fw fa-comment me-1 invisible"></i><span>Examination place: ${(examinationPlace(query.status.advanceControlNotification))}</span>`,
              summary: `<span>Control scheduled at: ${this.formatDate(query.status.advanceControlNotification.scheduledControlTime)}</span><br/><i class="fa-light fa-fw fa-comment me-1 invisible"></i><span>Examination place: ${(examinationPlace(query.status.advanceControlNotification))}</span>`,
              pointers: []
            }];
          default:
            return [];
        }
      default:
        return [];
    }
  }

  @HandleQuery("getTemporaryStorageErrors")
  getTemporaryStorageErrors(query: GetTemporaryStorageErrors): Observable<ErrorWithPointers[]> {
    return of((query.tsFilingStatus || query.tsStatus?.status) === IE3TemporaryStorageFilingStatus.REJECTED
      ? ConsignmentTsUtils.mergeAddressErrors(
          query.tsStatus?.registrationResponse?.errors?.map(e => {
            const pointers = ConsignmentTsUtils.parseErrorPointers(query.consignmentProcess, e.tag);
            return <ErrorWithPointers>{
              header: `${e.description}${e.code ? ' (' + e.code + ')' : ''}`,
              summary: ConsignmentTsUtils.formatSummary(e.description, e.code, pointers),
              pointers
            };
          })
        )
      : []);
  }

  private formatDate = (dateString: string): string => !!dateString ? moment(dateString).format("DD MMM HH:mm") : null;
}

export interface GetErrorPointers extends GetEnsErrors {
  tsFilingStatus?: IE3TemporaryStorageFilingStatus;
}

export interface GetEnsErrors {
  consignmentProcess: IE3ConsignmentProcess;
  taskStatus: TaskMessageStatus;
  status: IE3ConsignmentStatus;
}

export interface GetTemporaryStorageErrors {
  consignmentProcess: IE3ConsignmentProcess;
  tsStatus: IE3FilingStatusTemporaryStorage;
  tsFilingStatus?: IE3TemporaryStorageFilingStatus;
}
